import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ContentLayout } from '@app/layouts'
import { LntButton, LntDropdown } from '@app/components/base'
import { CommonExportLogReportModal } from '@app/modals'
import { Icon, Show } from '@app/components/util'
import { CheckingPricesCompetitorsDrawerRenderer } from '@app/components/renderers'
import { ExtendedTable, Header, Subheader, SubheaderToolbarDivider, TableSettingsToolbar } from '@app/components'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { MONITORING_VALIDATION_KEY } from '@app/constants/queryKeys'
import { FILE_TYPES, MONITORING_TASK_VALIDATION_CONFIG_NAMES } from '@app/api/constants'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { ICON_NAME } from '@app/constants/icon'

import { exportCheckingPricesFile, exportTechCheckingFile } from '@app/utils/export/exportFile'

import { useNotification, usePaginationQueryParams, useTableSearch, useTableSetting, useToggle } from '@app/hooks'

import {
  getMonitoringValidationResultsConfig,
  patchMonitoringValidationResultsConfig
} from '@app/api/routes/monitoringValidation'

import {
  getColumns,
  getLogReportDropdownItems,
  adaptCheckingPricesCompetitorsTableData,
  adaptCommonExportExcelLogReportParams
} from './lib'
import { useConfigValueColumn } from './hooks'
import { columnsSearchSchema, tableSearchQuerySchema } from './configs'

import type { Key } from 'react'
import type { RowSelectionType } from 'antd/es/table/interface'
import type { PatchMonitoringValidationResultsConfigData } from '@app/api/routes/monitoringValidation/types'
import type { LogReportData, CheckingPricesCompetitorsDataSource } from './types'

const columns = getColumns()

const title = 'Проверка цен конкурентов'

export const CheckingPricesCompetitorsPage: React.FC = () => {
  const exportCheckingPricesToggle = useToggle()
  const exportTechnicalCheckToggle = useToggle()

  const buttonLoadingExportCheckingToggle = useToggle()
  const buttonLoadingExportTechnicalToggle = useToggle()

  const { value: isDrawerOpen, setUntoggled: handleCloseDrawer, setToggled: handleOpenDrawer } = useToggle()

  const [selectedId, setIsSelectedId] = useState<keyof typeof MONITORING_TASK_VALIDATION_CONFIG_NAMES>()

  const { paginationParams, pagination, setPagination } = usePaginationQueryParams()
  const queryClient = useQueryClient()
  const notification = useNotification()

  const { data: checkingPricesCompetitorsTableData, isLoading: isTasksValidationsConfigDataLoading } = useQuery({
    queryKey: [MONITORING_VALIDATION_KEY.resultsConfig, paginationParams],
    queryFn: async () => await getMonitoringValidationResultsConfig(paginationParams),
    select: monitoringValidationResultsConfigData =>
      adaptCheckingPricesCompetitorsTableData(monitoringValidationResultsConfigData)
  })

  const patchTasksValidationsConfigMutation = useMutation({
    mutationFn: patchMonitoringValidationResultsConfig,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_VALIDATION_KEY.resultsConfig, paginationParams] })

      notification.success({
        message: 'Успешно сохранено'
      })
    }
  })

  const handleChangeValidationConfig = (data: PatchMonitoringValidationResultsConfigData): void => {
    patchTasksValidationsConfigMutation.mutate(data)
  }

  const handleExportCheckingPrices = async (data: LogReportData): Promise<void> => {
    buttonLoadingExportCheckingToggle.setToggled()

    const params = adaptCommonExportExcelLogReportParams(data)
    const fileName = 'Подготовка цен'

    try {
      await exportCheckingPricesFile(fileName, { ...params, file: FILE_TYPES.xlsx })
    } finally {
      buttonLoadingExportCheckingToggle.setUntoggled()
    }
  }

  const handleExportTechChecking = async (data: LogReportData): Promise<void> => {
    buttonLoadingExportTechnicalToggle.setToggled()

    const params = adaptCommonExportExcelLogReportParams(data)
    const fileName = 'Технические проверки'

    try {
      await exportTechCheckingFile(fileName, { ...params, file: FILE_TYPES.xlsx })
    } finally {
      buttonLoadingExportTechnicalToggle.setUntoggled()
    }
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setIsSelectedId(selectedKeys.toString() as keyof typeof MONITORING_TASK_VALIDATION_CONFIG_NAMES)
  }

  const dropdownItems = getLogReportDropdownItems({
    onClickTechnicalItem: exportTechnicalCheckToggle.setToggled,
    onClickPricesItem: exportCheckingPricesToggle.setToggled
  })

  const subheaderToolbar = (
    <LntDropdown
      trigger={ ['click'] }
      items={ dropdownItems }
    >
      <LntButton
        type={ BUTTON_TYPE.secondary }
        size={ SIZE.small }
      >
        Отчет по логам
        <Icon name={ ICON_NAME.arrowDown } />
      </LntButton>
    </LntDropdown>
  )

  useConfigValueColumn(columns, handleChangeValidationConfig)

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.checkingPricesCompetitors, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredTasksValidationsConfigDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: checkingPricesCompetitorsTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar = [
    <Show
      key='edit'
      condition={ !!selectedId?.length }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleOpenDrawer }
      >
        Редактировать
      </LntButton>

      <SubheaderToolbarDivider />
    </Show>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  const rowSelection = {
    type: 'radio' as RowSelectionType,
    onChange: handleSelectedRowChange,
    getCheckboxProps: (record: CheckingPricesCompetitorsDataSource) => ({
      disabled: record.configName === MONITORING_TASK_VALIDATION_CONFIG_NAMES.recalculationWeightAndVolumeIsEnabled
    })
  }

  return (
    <>
      <Header controlProps={ { title } } />

      <Subheader toolbar={ subheaderToolbar } />

      <ContentLayout>
        <ExtendedTable
          loading={ isTasksValidationsConfigDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredTasksValidationsConfigDataSource }
          toolbar={ toolbar }
          rowSelection={ rowSelection }
          pagination={ {
            ...pagination,
            total: checkingPricesCompetitorsTableData?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>

      <CommonExportLogReportModal
        open={ exportCheckingPricesToggle.value }
        onCancel={ exportCheckingPricesToggle.setUntoggled }
        onSubmit={ handleExportCheckingPrices }
        loading={ buttonLoadingExportCheckingToggle.value }
      />

      <CommonExportLogReportModal
        open={ exportTechnicalCheckToggle.value }
        onCancel={ exportTechnicalCheckToggle.setUntoggled }
        onSubmit={ handleExportTechChecking }
        loading={ buttonLoadingExportTechnicalToggle.value }
      />

      <CheckingPricesCompetitorsDrawerRenderer
        open={ isDrawerOpen }
        selectedId={ selectedId }
        onClose={ handleCloseDrawer }
      />
    </>
  )
}
